class AtlasLabScrollTabs extends HTMLElement {
  constructor() {
    super();
    this.ticking = false;
    this.trackTop = 0;
    this.distance = 1;
    this.storyLayout = null;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    if (this.dataset.atlasScrollTabsReady === 'true') return;
    this.dataset.atlasScrollTabsReady = 'true';

    this.track = this.querySelector('.atlas-scroll-tabs__track');
    this.sticky = this.querySelector('.atlas-scroll-tabs__sticky');
    this.frame = this.querySelector('[data-atlas-frame]');
    this.introLayer = this.querySelector('[data-stage-media="intro"]');
    this.intro = this.querySelector('[data-atlas-intro]');
    this.shell = this.querySelector('[data-atlas-shell]');
    this.storyMediaStage = this.querySelector('[data-atlas-story-media]');
    this.copyViewport = this.querySelector('.atlas-scroll-tabs__copy-viewport');
    this.copyTrack = this.querySelector('.atlas-scroll-tabs__copy-track');
    this.storyMediaItems = Array.from(this.querySelectorAll('[data-story-media-item]'));
    this.storyCopyItems = Array.from(this.querySelectorAll('[data-story-copy-item]'));

    if (!this.track || !this.sticky || !this.copyViewport || !this.copyTrack) return;

    this.panelCount = Math.max(this.storyMediaItems.length, this.storyCopyItems.length, 1);
    this.introShare = this.readNum(this.dataset.introShare, 0.34);
    this.frameShiftY = this.readNum(this.dataset.frameYShift, 0);
    this.mediaScaleMax = this.readNum(this.dataset.mediaScaleMax, 1.08);
    this.headerOffset = this.readCssNumber('--atlas-scroll-header-offset', 60);
    this.frameRadiusDesktop = this.readCssNumber('--atlas-scroll-frame-radius', 18);
    this.frameRadiusMobile = this.readCssNumber('--atlas-scroll-frame-radius-mobile', 14);
    this.storyLeadShare = this.panelCount > 1 ? 0.36 : 1;
    this.activeIndex = 0;

    this.collapseLength = this.introShare * 2;
    this.storyLength = (1 - this.introShare) * 1.15;
    this.totalScrollFactor = this.collapseLength + this.storyLength;

    this.style.setProperty('--atlas-scroll-track-multiplier', this.totalScrollFactor.toFixed(4));

    this.measure();
    this.update();

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => {
        this.measure();
        this.requestTick();
      });
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  onScroll() {
    this.requestTick();
  }

  onResize() {
    this.measure();
    this.requestTick();
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  }

  measure() {
    const rect = this.track.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    this.trackTop = rect.top + scrollY - this.headerOffset;
    const stageHeight = this.sticky.offsetHeight || window.innerHeight;
    this.distance = Math.max(this.track.offsetHeight - stageHeight, 1);
    this.storyLayout = this.measureStoryLayout();
  }

  measureStoryLayout() {
    if (!this.copyViewport || !this.storyCopyItems.length) return null;

    const viewportHeight = this.copyViewport.offsetHeight || this.readCssNumber('--atlas-story-height', 520);
    const centerY = viewportHeight / 2;
    const itemMetrics = this.storyCopyItems.map((item) => {
      const top = item.offsetTop;
      const height = item.offsetHeight;
      return {
        item,
        top,
        height,
        center: top + height / 2,
      };
    });

    const centerTranslates = itemMetrics.map((metric, index) => {
      const target = centerY - metric.center;
      return index === 0 ? target + 100 : target;
    });
    const introOffset = Math.min(140, viewportHeight * 0.12);

    return {
      viewportHeight,
      centerY,
      startTranslate: centerTranslates[0] + introOffset,
      centerTranslates,
      itemMetrics,
    };
  }

  update() {
    this.ticking = false;

    const scrollY = window.scrollY || window.pageYOffset;
    const progress = this.clamp((scrollY - this.trackTop) / this.distance, 0, 1);
    const virtualProgress = progress * this.totalScrollFactor;
    const introProgress = this.clamp(virtualProgress / Math.max(this.collapseLength, 0.001), 0, 1);
    const collapseProgress = this.smooth01(introProgress);
    const frameState = this.computeFrameState(collapseProgress);
    const currentFrameWidth = (this.sticky.clientWidth || window.innerWidth) - frameState.left - frameState.right;
    const currentFrameHeight = (this.sticky.clientHeight || window.innerHeight) - frameState.top - frameState.bottom;
    const widthRatio = currentFrameWidth / Math.max(frameState.targetWidth || currentFrameWidth, 1);
    const heightRatio = currentFrameHeight / Math.max(frameState.targetHeight || currentFrameHeight, 1);
    const frameRatio = Math.max(widthRatio, heightRatio);
    const shellProgress = this.smooth01(this.clamp((1.2 - frameRatio) / 0.2, 0, 1));
    const storyProgress = this.getStoryProgress(progress, shellProgress);
    const storyState = this.computeStoryState(storyProgress);
    const introTextOpacity = 1 - this.smooth01(this.clamp((collapseProgress - 0.08) / 0.6, 0, 1));
    const introMediaOpacity = 1 - this.smooth01(this.clamp((collapseProgress - 0.56) / 0.38, 0, 1));
    const mediaScale = this.lerp(1, this.mediaScaleMax, collapseProgress);
    const mediaShiftX = this.lerp(0, frameState.mediaShiftX, collapseProgress);

    this.style.setProperty('--atlas-intro-opacity', introTextOpacity.toFixed(4));
    this.style.setProperty('--atlas-shell-opacity', shellProgress.toFixed(4));
    this.style.setProperty('--atlas-media-scale', mediaScale.toFixed(4));
    this.style.setProperty('--atlas-media-shift-x', `${mediaShiftX.toFixed(2)}px`);
    this.style.setProperty('--atlas-frame-top', `${frameState.top.toFixed(2)}px`);
    this.style.setProperty('--atlas-frame-right', `${frameState.right.toFixed(2)}px`);
    this.style.setProperty('--atlas-frame-bottom', `${frameState.bottom.toFixed(2)}px`);
    this.style.setProperty('--atlas-frame-left', `${frameState.left.toFixed(2)}px`);
    this.style.setProperty('--atlas-frame-radius-current', `${frameState.radius.toFixed(2)}px`);

    this.syncStageVisibility(introMediaOpacity, shellProgress);
    this.applyCopyState(storyState, shellProgress);
    this.applyMediaState(storyState, shellProgress);

    if (storyState.dominantIndex !== this.activeIndex || !this.initializedState) {
      this.activeIndex = storyState.dominantIndex;
      this.initializedState = true;
    }

    this.toggleVideos(storyState.activeMediaIndices, shellProgress > 0.05);
  }

  syncStageVisibility(introMediaOpacity, shellProgress) {
    if (this.introLayer) {
      const visible = introMediaOpacity > 0.001;
      this.introLayer.classList.toggle('is-active', visible);
      this.introLayer.style.opacity = introMediaOpacity.toFixed(4);
      this.introLayer.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if (this.storyMediaStage) {
      this.storyMediaStage.classList.toggle('is-interactive', shellProgress > 0.05);
    }

    if (this.shell) {
      this.shell.classList.toggle('is-interactive', shellProgress > 0.55);
    }
  }

  applyCopyState(storyState, shellProgress) {
    if (!this.storyLayout || !this.copyTrack) return;

    const { itemMetrics } = this.storyLayout;

    this.copyTrack.style.transform = `translate3d(0, ${storyState.translate.toFixed(2)}px, 0)`;

    itemMetrics.forEach((metric, index) => {
      metric.item.style.opacity = '1';
      metric.item.classList.toggle('is-active', true);
      metric.item.setAttribute('aria-hidden', 'false');
      metric.item.style.pointerEvents = shellProgress > 0.55 ? 'auto' : 'none';

      if (index === storyState.baseIndex || index === storyState.coverIndex) {
        metric.item.style.zIndex = '1';
      } else {
        metric.item.style.zIndex = '0';
      }
    });
  }

  applyMediaState(storyState, shellProgress) {
    if (!this.storyMediaItems.length) return;

    this.storyMediaItems.forEach((item, index) => {
      const isBase = index === storyState.baseIndex;
      const isCover = index === storyState.coverIndex && storyState.coverIndex !== storyState.baseIndex;
      const active = shellProgress > 0.02 && (isBase || isCover);

      let opacity = 0;
      let clipTop = 100;
      let zIndex = 0;

      if (isBase) {
        opacity = 1;
        clipTop = 0;
        zIndex = 1;
      }

      if (isCover) {
        opacity = 1;
        clipTop = (1 - storyState.coverProgress) * 100;
        zIndex = 2;
      }

      item.style.opacity = opacity.toFixed(4);
      item.style.clipPath = `inset(${clipTop.toFixed(4)}% 0 0 0)`;
      item.style.zIndex = String(zIndex);
      item.classList.toggle('is-active', active);
      item.classList.toggle('is-base', isBase);
      item.classList.toggle('is-cover', isCover);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  toggleVideos(activeIndices, allowPlayback) {
    const activeSet = new Set(allowPlayback ? activeIndices : []);
    this.storyMediaItems.forEach((item, index) => {
      item.querySelectorAll('video').forEach((video) => {
        if (activeSet.has(index)) {
          const playPromise = video.play?.();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause?.();
        }
      });
    });
  }

  computeStoryState(progress) {
    if (!this.storyLayout || !this.storyLayout.itemMetrics.length) {
      return {
        translate: 0,
        baseIndex: 0,
        coverIndex: 0,
        coverProgress: 0,
        dominantIndex: 0,
        activeMediaIndices: [0],
      };
    }

    const { startTranslate, centerTranslates, itemMetrics } = this.storyLayout;

    if (this.panelCount <= 1 || progress <= this.storyLeadShare) {
      const local = this.panelCount <= 1 ? 1 : this.smooth01(progress / Math.max(this.storyLeadShare, 0.001));
      return {
        translate: this.lerp(startTranslate, centerTranslates[0], local),
        baseIndex: 0,
        coverIndex: 0,
        coverProgress: 0,
        dominantIndex: 0,
        activeMediaIndices: [0],
      };
    }

    const segmentSize = (1 - this.storyLeadShare) / Math.max(this.panelCount - 1, 1);
    const rawPosition = this.clamp(
      (progress - this.storyLeadShare) / Math.max(segmentSize, 0.001),
      0,
      this.panelCount - 1
    );
    const segmentIndex = Math.min(this.panelCount - 2, Math.floor(rawPosition));
    const local = this.smooth01(this.clamp(rawPosition - segmentIndex, 0, 1));
    const coverIndex = segmentIndex + 1;

    const fromTranslate = centerTranslates[segmentIndex];
    const toTranslate = centerTranslates[coverIndex];
    const translate = this.lerp(fromTranslate, toTranslate, local);
    const currentMetric = itemMetrics[segmentIndex];
    const currentTop = currentMetric ? currentMetric.top + translate : 0;
    const allowCover = currentTop <= 0;
    const gateTranslate = currentMetric ? -currentMetric.top : fromTranslate;
    const gateLocal =
      Math.abs(toTranslate - fromTranslate) < 0.001
        ? 1
        : this.clamp((gateTranslate - fromTranslate) / (toTranslate - fromTranslate), 0, 1);
    const coverProgress = allowCover
      ? this.clamp((local - gateLocal) / Math.max(1 - gateLocal, 0.001), 0, 1)
      : 0;

    return {
      translate,
      baseIndex: segmentIndex,
      coverIndex,
      coverProgress,
      dominantIndex: coverProgress >= 0.5 ? coverIndex : segmentIndex,
      activeMediaIndices: coverProgress > 0.001 ? [segmentIndex, coverIndex] : [segmentIndex],
    };
  }

  getStoryProgress(progress, shellProgress = 1) {
    const virtualProgress = progress * this.totalScrollFactor;
    const introStoryRaw = this.clamp(virtualProgress / Math.max(this.collapseLength, 0.001), 0, 1);
    const introStoryProgress = Math.pow(introStoryRaw, 0.72) * shellProgress;

    if (virtualProgress <= this.collapseLength) {
      return this.storyLeadShare * introStoryProgress;
    }

    const followProgress = this.clamp(
      (virtualProgress - this.collapseLength) / Math.max(this.storyLength, 0.001),
      0,
      1
    );

    return this.storyLeadShare + followProgress * (1 - this.storyLeadShare);
  }

  computeFrameState(progress) {
    const viewportWidth = this.sticky.clientWidth || window.innerWidth;
    const stageHeight = this.sticky.clientHeight || window.innerHeight;
    const isMobile = window.innerWidth <= 767;
    const isTablet = window.innerWidth > 767 && window.innerWidth <= 1023;

    if (!isMobile && !isTablet) {
      const storyWidthToken = this.readCssNumber('--atlas-story-width', 1200);
      const sideOffset = this.readCssNumber('--atlas-story-side-offset', 48);
      const gap = this.readCssNumber('--atlas-story-gap', 16);
      const endWidth = this.readCssNumber('--atlas-story-media-width', 692);
      const endHeight = this.readCssNumber('--atlas-story-height', 520);
      const storyWidth = Math.min(storyWidthToken, Math.max(endWidth + gap, viewportWidth - sideOffset * 2));
      const copyWidth = Math.max(0, storyWidth - endWidth - gap);
      const groupLeft = (viewportWidth - storyWidth) / 2;
      const endLeft = groupLeft + copyWidth + gap;
      const endTop = (stageHeight - endHeight) / 2 + this.frameShiftY;
      const endBottom = stageHeight - endTop - endHeight;
      const endRight = viewportWidth - endLeft - endWidth;

      return {
        top: this.lerp(0, endTop, progress),
        right: this.lerp(0, endRight, progress),
        bottom: this.lerp(0, endBottom, progress),
        left: this.lerp(0, endLeft, progress),
        radius: this.lerp(0, this.frameRadiusDesktop, progress),
        mediaShiftX: 0,
        targetWidth: endWidth,
        targetHeight: endHeight,
      };
    }

    const endRadius = isMobile ? this.frameRadiusMobile : this.frameRadiusDesktop;
    const frameWidthToken = isMobile
      ? this.readCssNumber('--atlas-scroll-frame-width-mobile', 360)
      : this.readCssNumber('--atlas-scroll-frame-width-pad', 980);
    const horizontalMargin = isMobile ? 16 : 28;
    const maxFrameWidth = viewportWidth - horizontalMargin * 2;
    const endWidth = Math.max(260, Math.min(frameWidthToken, maxFrameWidth));
    const endHeight = isMobile
      ? Math.min(stageHeight * 0.34, endWidth * 0.72)
      : Math.min(stageHeight * 0.44, endWidth * 0.56);
    const endRight = horizontalMargin;
    const endLeft = viewportWidth - endRight - endWidth;
    const centeredTop = (stageHeight - endHeight) / 2;
    const endTop = isMobile ? Math.max(90, stageHeight * 0.42 + this.frameShiftY * 0.4) : centeredTop + this.frameShiftY;
    const endBottom = stageHeight - endTop - endHeight;

    return {
      top: this.lerp(0, endTop, progress),
      right: this.lerp(0, endRight, progress),
      bottom: this.lerp(0, endBottom, progress),
      left: this.lerp(0, endLeft, progress),
      radius: this.lerp(0, endRadius, progress),
      mediaShiftX: 0,
      targetWidth: endWidth,
      targetHeight: endHeight,
    };
  }

  readNum(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  readCssNumber(name, fallback) {
    const value = getComputedStyle(this).getPropertyValue(name).trim().replace('px', '');
    return this.readNum(value, fallback);
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  lerp(from, to, t) {
    return from + (to - from) * t;
  }

  smooth01(value) {
    const t = this.clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  }
}

if (!window.customElements.get('atlas-lab-scroll-tabs')) {
  window.customElements.define('atlas-lab-scroll-tabs', AtlasLabScrollTabs);
}
